import ky, { type KyInstance } from 'ky';
import { useAuthStore } from '@store/auth';

/**
 * Field name → Türkçe etiket eşlemesi.
 * Eksik field'lar olduğu gibi gösterilir.
 */
const FIELD_LABELS: Record<string, string> = {
  ad: 'Ad',
  soyad: 'Soyad',
  unvan: 'Ünvan',
  email: 'E-posta',
  telefon: 'Telefon',
  telefon2: 'İkinci telefon',
  vergiNo: 'Vergi No',
  vergiDairesi: 'Vergi Dairesi',
  websitesi: 'Web sitesi',
  sektor: 'Sektör',
  segment: 'Segment',
  etiketler: 'Etiketler',
  adres: 'Adres',
  sehir: 'Şehir',
  ulke: 'Ülke',
  postaKodu: 'Posta Kodu',
  notlar: 'Notlar',
  tipi: 'Müşteri Tipi',
  firmaAdi: 'Firma Adı',
  baslik: 'Başlık',
  tutar: 'Tutar',
  asama: 'Aşama',
  tarih: 'Tarih',
  kvkkAcikRiza: 'KVKK Onayı',
};

/**
 * class-validator İngilizce hata mesajını Türkçe açıklamaya çevirir.
 * Tanınmayan (validation olmayan) mesajlar için null döner — böylece
 * backend'in dostça Türkçe mesajları ("Krediniz bitti — devamı Pro'da." gibi)
 * "alan: açıklama" formatına bozulmadan olduğu gibi gösterilir.
 */
function trDescription(msg: string): string | null {
  if (/must be an email/i.test(msg)) return 'geçerli bir e-posta olmalı';
  if (/must be a (string|valid string)/i.test(msg)) return 'metin olmalı';
  if (/must be a number/i.test(msg)) return 'sayı olmalı';
  if (/must be a boolean/i.test(msg)) return 'doğru/yanlış olmalı';
  if (/must be a UUID/i.test(msg)) return 'geçerli bir kimlik (UUID) olmalı';
  if (/must be one of the following values/i.test(msg)) {
    return 'geçerli bir seçenek olmalı';
  }
  if (/must be longer than or equal to (\d+)/i.test(msg)) {
    const n = msg.match(/(\d+)/)?.[1];
    return `en az ${n} karakter olmalı`;
  }
  if (/must be shorter than or equal to (\d+)/i.test(msg)) {
    const n = msg.match(/(\d+)/)?.[1];
    return `en fazla ${n} karakter olabilir`;
  }
  if (/should not be empty/i.test(msg)) return 'boş bırakılamaz';
  if (/should not exist/i.test(msg)) return 'tanımsız alan';
  if (/must be an array/i.test(msg)) return 'liste olmalı';
  if (/must be an object/i.test(msg)) return 'obje olmalı';
  return null; // validation kalıbı değil — mesajı dönüştürme
}

/**
 * class-validator mesajı "field must be ..." formatında. Field'ı çekip Türkçe etikete çevir.
 */
function translateValidationMessage(msg: string): string {
  // forbidNonWhitelisted özel format: "property <field> should not exist"
  const forbidden = msg.match(/^property (\w+) should not exist$/i);
  if (forbidden) {
    const f = forbidden[1] ?? '';
    const label = FIELD_LABELS[f] ?? f;
    return `${label}: bu alan kabul edilmiyor`;
  }

  const match = msg.match(/^([a-zA-Z][a-zA-Z0-9_]*) (.+)$/);
  if (!match) return msg;
  const fieldName = match[1] ?? '';
  const rest = match[2] ?? '';
  const aciklama = trDescription(rest);
  // Validation kalıbı değilse (örn. backend'in dostça Türkçe iş mesajları)
  // mesajı olduğu gibi göster — "alan: açıklama" formatına zorlamak bozuyor.
  if (aciklama === null) return msg;
  const label = FIELD_LABELS[fieldName] ?? fieldName;
  return `${label}: ${aciklama}`;
}

/**
 * Lisans pasif (salt-okunur) firmada değiştiren istek 403 + kod LISANS_PASIF
 * döner. İstemci bu olayı window'a yayar; @features/lisans bildirim gösterir.
 */
export const LISANS_PASIF_OLAYI = 'zoprey:lisans-pasif';

/**
 * Backend HTTP istemcisi.
 * - Supabase JWT Authorization header'ı otomatik (KARAR-005 sonrası)
 * - tenantId JWT payload'tan backend tarafında çıkarılır (X-Tenant-ID artık yok)
 * - Hata yönetimi: NestJS default `{statusCode, message, error}` + custom `{errors:[...]}` formatlarını yakalar
 */
export const api: KyInstance = ky.create({
  prefixUrl: import.meta.env.VITE_API_URL ?? '/api/v1',
  timeout: 30_000,
  retry: { limit: 2, methods: ['get'] },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().token();
        if (token) request.headers.set('Authorization', `Bearer ${token}`);
        request.headers.set('Accept-Language', 'tr');
      },
    ],
    beforeError: [
      async (error) => {
        if (!error.response) return error;
        try {
          const body = await error.response.json<{
            // Custom format (auth-service tarzı)
            errors?: Array<{ code: string; message: string; field?: string }>;
            // NestJS default format
            statusCode?: number;
            message?: string | string[];
            error?: string;
            // Uygulama hata kodu (ör. LISANS_PASIF)
            kod?: string;
          }>();

          if (body?.kod === 'LISANS_PASIF') {
            window.dispatchEvent(new CustomEvent(LISANS_PASIF_OLAYI));
          }

          // 1) Custom format öncelikli
          if (body?.errors?.[0]) {
            error.message = body.errors[0].message;
            (error as { code?: string }).code = body.errors[0].code;
            return error;
          }

          // 2) NestJS default format
          if (body?.message) {
            const messages = Array.isArray(body.message) ? body.message : [body.message];
            const translated = messages.map(translateValidationMessage);
            error.message = translated.join(' • ');
            (error as { code?: string }).code =
              body.kod ?? `HTTP_${body.statusCode ?? error.response.status}`;
            return error;
          }
        } catch {
          // body parse edilemedi — fallback olarak HTTP status mesajı
          error.message = `Sunucu ${error.response.status} döndü`;
        }
        return error;
      },
    ],
  },
});

export interface ListResponse<T> {
  data: T[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}
